import { PlayerGameStatus, SessionStatus } from '@/API';
import ZoomInviteButton from '@/components/Zoom/ZoomInviteButton';
import PlayerCanvas from '@/components/Player/PlayerCanvas';
import PlayerSpotlight from '@/components/Player/PlayerSpotlight';
import { useMultiplayerDemo } from '@/contexts/MultiplayerDemoContext';
import NoSleepProvider from '@/hooks/NoSleep';
import { usePlayer, useSession } from '@/hooks/PlayerSessionControlHooks';
import ZoomMultiplayerProvider from '@/providers/ZoomMultiplayerProvider';
import { MULTIPLAYER_PROMPT } from '@/zoom/multiplayerProtocol';
import { Link, useSearchParams } from 'react-router-dom';

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary-clementine-100 px-16">
      <div className="w-full rounded-xs bg-white p-20 text-center shadow-sm">{children}</div>
    </div>
  );
}

function MultiplayerStage() {
  const multiplayer = useMultiplayerDemo();
  const session = useSession();
  const player = usePlayer();

  if (!multiplayer.available) {
    return (
      <CenteredCard>
        <h1 className="text-h3 mb-8">Multiplayer unavailable</h1>
        <p className="text-body text-body-sm mb-16">
          This requires Zoom desktop with in-meeting app messaging enabled.
        </p>
        <Link to="/zoom" className="btn btn-neutral w-full">
          Back
        </Link>
      </CenteredCard>
    );
  }

  if (multiplayer.phase === 'ended') {
    return (
      <CenteredCard>
        <h1 className="text-h3 mb-8">Demo complete</h1>
        <p className="text-body text-body-sm mb-16">Thanks for playing together.</p>
        <Link to="/zoom" className="btn btn-primary w-full">
          Return to Scribl
        </Link>
      </CenteredCard>
    );
  }

  if (multiplayer.phase === 'connecting' || !session || !player) {
    return (
      <CenteredCard>
        <h1 className="text-h3 mb-8">Joining game</h1>
        <p className="text-body text-body-sm">Waiting for the Zoom meeting host…</p>
      </CenteredCard>
    );
  }

  if (multiplayer.phase === 'lobby') {
    return (
      <CenteredCard>
        <h1 className="text-h3 mb-4">Multiplayer demo</h1>
        <p className="text-body text-body-sm mb-16">
          {multiplayer.isHost
            ? 'Invite someone in this meeting to join.'
            : 'The host is setting up the game.'}
        </p>

        <div className="mb-16 rounded-xs bg-neutral-100 p-12 text-left">
          <p className="text-body text-body-xs-strong mb-8">
            Players ({multiplayer.players.length})
          </p>
          <ul className="flex flex-col gap-4">
            {multiplayer.players.map((item) => (
              <li key={item.id} className="text-body text-body-sm">
                {item.name}
                {item.id === multiplayer.currentPlayerId ? ' (you)' : ''}
              </li>
            ))}
          </ul>
        </div>

        {multiplayer.error && (
          <p className="text-body text-body-xs mb-12 rounded-xs bg-red-50 p-8 text-red-700">
            {multiplayer.error}
          </p>
        )}

        {multiplayer.isHost ? (
          <div className="flex flex-col gap-8">
            <ZoomInviteButton className="btn btn-neutral w-full" />
            <button
              type="button"
              className="btn btn-primary w-full"
              disabled={multiplayer.players.length < 2}
              onClick={multiplayer.startGame}
            >
              {multiplayer.players.length < 2 ? 'Waiting for one player…' : 'Start drawing'}
            </button>
          </div>
        ) : (
          <p className="text-body text-body-xs text-neutral-400">Waiting for the host to start…</p>
        )}
      </CenteredCard>
    );
  }

  if (session.status === SessionStatus.drawing) {
    if (player.gameStatus === PlayerGameStatus.DRAWING) {
      return (
        <div className="relative h-screen">
          <PlayerCanvas promptText={MULTIPLAYER_PROMPT} />
          {multiplayer.error && (
            <p className="text-body text-body-xs absolute bottom-4 left-8 right-8 z-100 rounded-xs bg-red-50 p-8 text-center text-red-700">
              {multiplayer.error}
            </p>
          )}
        </div>
      );
    }

    return (
      <CenteredCard>
        <h1 className="text-h3 mb-8">Drawing submitted</h1>
        <p className="text-body text-body-sm mb-16">
          {multiplayer.allSubmitted ? 'Everyone is ready.' : 'Waiting for the other players…'}
        </p>
        {multiplayer.isHost && multiplayer.hasSubmissions && (
          <button
            type="button"
            className="btn btn-primary w-full"
            onClick={multiplayer.showNextDrawing}
          >
            {multiplayer.allSubmitted ? 'Show the first drawing' : 'Show submitted drawings now'}
          </button>
        )}
      </CenteredCard>
    );
  }

  if (session.status === SessionStatus.sharing) {
    if (!multiplayer.hasCurrentArtwork) {
      return (
        <CenteredCard>
          <h1 className="text-h3 mb-8">Loading drawing</h1>
          <p className="text-body text-body-sm">Receiving artwork from the host…</p>
        </CenteredCard>
      );
    }

    return (
      <div className="relative h-screen overflow-y-auto">
        <PlayerSpotlight isSharing={player.gameStatus === PlayerGameStatus.SHARING} />
        {multiplayer.isHost && (
          <div className="absolute right-8 top-8 z-100 flex gap-4">
            <button
              type="button"
              className="text-body text-body-xs rounded-xs bg-white/90 px-8 py-4"
              onClick={multiplayer.showNextDrawing}
            >
              Next
            </button>
            <button
              type="button"
              className="text-body text-body-xs rounded-xs bg-white/90 px-8 py-4"
              onClick={multiplayer.endGame}
            >
              End
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <CenteredCard>
      <h1 className="text-h3 mb-8">Demo complete</h1>
      <p className="text-body text-body-sm mb-16">Thanks for playing together.</p>
      <Link to="/zoom" className="btn btn-primary w-full">
        Return to Scribl
      </Link>
    </CenteredCard>
  );
}

export default function ZoomMultiplayer() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('host') === '1' ? 'host' : 'guest';

  return (
    <NoSleepProvider>
      <ZoomMultiplayerProvider
        mode={mode}
        guestGameId={searchParams.get('game') ?? undefined}
        guestHostId={searchParams.get('host') ?? undefined}
      >
        <MultiplayerStage />
      </ZoomMultiplayerProvider>
    </NoSleepProvider>
  );
}
